"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import { client } from "@/sanity/lib/client";
import { urlForImage } from "@/sanity/lib/image";
import NewsletterForm from "@/app/components/NewsletterForm";

interface SanityPost {
  _id: string;
  title: string;
  slug: { current: string };
  mainImage: any;
  publishedAt: string;
  excerpt: string;
  categories: { title: string }[];
  author: { name: string; image: any };
  tags: string[];
}

function CategoryBadge({ category }: { category: string }) {
  return (
    <span
      className="text-xs tracking-widest uppercase px-3 py-1"
      style={{ background: "rgba(201,168,76,0.15)", color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}
    >
      {category}
    </span>
  );
}

function PostCard({ post, index }: { post: SanityPost; index: number }) {
  const dateStr = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "Recently Published";

  const imageUrl = post.mainImage ? urlForImage(post.mainImage).url() : null;

  return (
    <motion.article
      key={post._id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.07 }}
      className="group cursor-pointer"
    >
      <Link href={`/blog/${post.slug.current}`}>
        <div
          className="relative h-48 mb-4 overflow-hidden"
          style={{ border: "1px solid rgba(201,168,76,0.15)" }}
        >
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={post.title} 
              className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-300" 
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(201,168,76,0.06)" }}>
              <span className="text-xs tracking-widest uppercase" style={{ color: "rgba(201,168,76,0.3)", fontFamily: "'Montserrat', sans-serif" }}>
                {post.categories?.[0]?.title || "Article"}
              </span>
            </div>
          )}
          <div className="absolute top-3 left-3">
            {post.categories?.[0] && <CategoryBadge category={post.categories[0].title} />}
          </div>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>{dateStr}</span>
          <span style={{ color: "rgba(201,168,76,0.2)" }}>·</span>
          <span className="text-xs" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>5 min read</span>
        </div>
        <h2
          className="text-xl font-light mb-2 group-hover:text-[#C9A84C] transition-colors duration-300"
          style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
        >
          {post.title}
        </h2>
        <p className="text-sm leading-relaxed line-clamp-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
          {post.excerpt}
        </p>
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {post.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[9px] uppercase tracking-wider px-2 py-0.5" style={{ background: "rgba(201,168,76,0.07)", color: "rgba(201,168,76,0.5)", fontFamily: "'Montserrat', sans-serif" }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </Link>
    </motion.article>
  );
}

export default function BlogClient() {
  const [posts, setPosts] = useState<SanityPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const query = activeCategory 
          ? `*[_type == "post" && "${activeCategory}" in categories[]->title] | order(publishedAt desc) {
              _id, title, slug, mainImage, publishedAt, excerpt, tags,
              "categories": categories[]->{title},
              "author": author->{name, image}
            }`
          : `*[_type == "post"] | order(publishedAt desc) {
              _id, title, slug, mainImage, publishedAt, excerpt, tags,
              "categories": categories[]->{title},
              "author": author->{name, image}
            }`;
        
        const result = await client.fetch(query);
        setPosts(result);

        // Fetch all categories for filter
        const catQuery = `*[_type == "category"].title`;
        const catResult = await client.fetch(catQuery);
        setCategories(catResult);
      } catch (err) {
        console.error("Sanity fetch error:", err);
      }
      setLoading(false);
    }
    fetchData();
  }, [activeCategory]);

  return (
    <>
      <Navbar />
      <main className="min-h-screen px-6 py-32" style={{ background: "#0A0908" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "rgba(201,168,76,0.7)", fontFamily: "'Montserrat', sans-serif" }}>
              Aurelius & Co Journal
            </p>
            <h1 className="text-4xl md:text-5xl font-light leading-tight mb-4" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>
              Insights &{" "}
              <span style={{ color: "#C9A84C", fontStyle: "italic" }}>Resources</span>
            </h1>
            <p className="text-sm leading-relaxed max-w-xl mx-auto" style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}>
              Expert insights on Instagram marketing, automation best practices, and growth strategies.
            </p>
          </div>

          {/* Category filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8 justify-center">
              <button
                onClick={() => setActiveCategory(null)}
                className="text-xs tracking-widest uppercase px-4 py-1.5 transition-all"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  background: !activeCategory ? "rgba(201,168,76,0.15)" : "transparent",
                  color: !activeCategory ? "#C9A84C" : "rgba(245,240,232,0.35)",
                  border: "1px solid",
                  borderColor: !activeCategory ? "rgba(201,168,76,0.3)" : "rgba(201,168,76,0.1)"
                }}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="text-xs tracking-widest uppercase px-4 py-1.5 transition-all"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    background: activeCategory === cat ? "rgba(201,168,76,0.15)" : "transparent",
                    color: activeCategory === cat ? "#C9A84C" : "rgba(245,240,232,0.35)",
                    border: "1px solid",
                    borderColor: activeCategory === cat ? "rgba(201,168,76,0.3)" : "rgba(201,168,76,0.1)"
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Posts grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: "rgba(201,168,76,0.2)", borderTopColor: "#C9A84C" }} />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-24 border border-[rgba(201,168,76,0.08)]" style={{ background: "rgba(201,168,76,0.02)" }}>
              <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "rgba(201,168,76,0.5)", fontFamily: "'Montserrat', sans-serif" }}>Coming Soon</p>
              <p className="text-3xl font-light mb-3" style={{ color: "rgba(245,240,232,0.5)", fontFamily: "'Cormorant', serif" }}>Blogs Coming Soon</p>
              <p className="text-xs" style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif" }}>We&apos;re crafting expert insights. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <PostCard key={post._id} post={post} index={i} />
              ))}
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-16 text-center">
            <p className="text-sm mb-6" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
              Want to stay updated with latest insights?
            </p>
            <NewsletterForm />
          </div>
        </motion.div>
      </main>
      <Footer />
    </>
  );
}
